import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Info, MailPlus, ShieldCheck, UserMinus, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { InviteMemberResponse, MemberRole, OrganizationUser, Role, UserRoleAssignment } from '../types/api';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ColumnPicker } from '../components/ColumnPicker';
import { PersonCell, personDisplayName } from '../components/PersonCell';
import { RoleChips } from '../components/RoleChips';
import { DateCell } from '../components/Timestamp';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { batchFetcher } from '../lib/listPage';
import { usePermissions } from '../hooks/usePermissions';
import { apiService } from '../services/api';
import { DialogActions, DialogForm } from '../components/DialogActions';
import { RowAction } from '../components/RowAction';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { DataTable, DataTableColumn, SearchInput, hasListError, isListRetrying, listTableProps } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ListEmptyState } from '../components/ListEmptyState';
import { ErrorState } from '../components/ErrorState';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import ConfirmDialog from '../components/ConfirmDialog';
import { ConsequenceAlert } from '../components/ConsequenceAlert';
import { Field } from '../components/Field';
import { CheckboxList } from '../components/CheckboxList';
import { classifyError, humanizeError } from '../lib/errors';
import { FormError } from '../components/FormError';
import { routeCopy } from '../routes';

/** h1 and description, from the route registry (DESIGN_SYSTEM.md §6 Naming). */
const PAGE = routeCopy('/my-org');

// ---------------------------------------------------------------------------
// Invite member dialog
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The system organization's own role (`isSystemOnly` — "System Administrator"
 * in production) is not an ordinary role and is never offered as one. Outside
 * the system organization the API withholds it entirely; inside, both dialogs
 * below fold it into a single "System administrator access" switch, off by
 * default, rather than listing it between "Org Admin" and "Application
 * Builder".
 */
const partitionRoles = (roles: Role[]) => ({
  standardRoles: roles.filter((role) => !role.isSystemOnly),
  systemRoles: roles.filter((role) => role.isSystemOnly),
});

const SYSTEM_ACCESS_LABEL = 'System administrator access';
const SYSTEM_ACCESS_HELP = 'Opens System Admin and reaches every organization.';

interface SystemAccessSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * The one control both dialogs use for it, so the two read the same.
 *
 * A Switch on both surfaces under the shared-control exception (DESIGN_SYSTEM
 * §5 Boolean controls), but it follows each surface's commit model: in Invite
 * member it rides with the invite, and in Manage roles it only changes the
 * draft that Save confirms. It never saves, or confirms, on its own.
 */
const SystemAccessSwitch: React.FC<SystemAccessSwitchProps> = ({
  checked, disabled, onCheckedChange,
}) => (
  <Field orientation="choice" label={SYSTEM_ACCESS_LABEL} help={SYSTEM_ACCESS_HELP}>
    {(control) => (
      <Switch
        {...control}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
      />
    )}
  </Field>
);

/** What granting system administrator access reaches, said where it is decided. */
const systemAccessConsequence = (who: string) =>
  `Opens System Admin for ${who} and reaches every organization on the platform.`;

interface RoleChecklistProps {
  /** Prefix for the checkbox ids, unique per dialog. */
  idPrefix: string;
  /** Ordinary RBAC roles only — never a system-only role, never the legacy role. */
  roles: Role[];
  selectedIds: Set<string>;
  onToggle: (roleId: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  loadError?: unknown;
  onRetry?: () => void;
  required?: boolean;
  /** The group's validation message ("Select at least one role."). */
  error?: string | null;
  /** Under the list. */
  help?: React.ReactNode;
}

/**
 * The ordinary-role checklist Invite member and Manage roles share: the kit
 * CheckboxList over an organization's roles, each with its description. The
 * roles are a bounded set, so they are passed whole (no batches). File-local
 * on purpose — CheckboxList is the kit piece; this only fixes the copy.
 */
const RoleChecklist: React.FC<RoleChecklistProps> = ({
  idPrefix, roles, selectedIds, onToggle, disabled, isLoading, loadError, onRetry, required, error, help,
}) => (
  <CheckboxList
    legend="Roles"
    itemNoun="roles"
    searchPlaceholder="Search roles…"
    idPrefix={idPrefix}
    items={roles.map((role) => ({ id: role.id, label: role.name, description: role.description || undefined }))}
    selectedIds={selectedIds}
    onToggle={onToggle}
    disabled={disabled}
    isLoading={isLoading}
    loadError={loadError}
    loadErrorTitle="Couldn't load roles"
    onRetry={onRetry}
    required={required}
    error={error}
    help={help}
    emptyMessage="No roles available."
  />
);

interface InviteMemberDialogProps {
  open: boolean;
  orgId: string;
  /** Whether `orgId` is the system organization — the only place the switch exists. */
  isSystemOrg: boolean;
  accessToken: string;
  onClose: () => void;
  /** The invite landed: the page pins the new member's row (no list reload). */
  onSuccess: (result: InviteMemberResponse, names: { firstName: string; lastName: string }) => void;
}

const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  open, orgId, isSystemOrg, accessToken, onClose, onSuccess,
}) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState<unknown>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState<{ first: string | null; last: string | null }>({
    first: null, last: null,
  });
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [systemAccess, setSystemAccess] = useState(false);
  const [roleSelectionError, setRoleSelectionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    setRolesError(null);
    try {
      const roleList = await apiService.listOrgRoles(orgId, accessToken);
      setRoles(roleList);
    } catch (err: unknown) {
      console.error('Failed to load roles:', err);
      setRolesError(err);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [orgId, accessToken]);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setFirstName('');
    setLastName('');
    setNameError({ first: null, last: null });
    setSelectedRoleIds(new Set());
    setSystemAccess(false);
    setEmailError(null);
    setRoleSelectionError(null);
    setSubmitError(null);
    loadRoles();
  }, [open, loadRoles]);

  const { standardRoles, systemRoles } = useMemo(() => partitionRoles(roles), [roles]);
  // Belt and braces: the API already withholds system-only roles from every
  // other organization, and one it did send is still not offered there —
  // neither as a checkbox nor as the switch.
  const offersSystemAccess = isSystemOrg && systemRoles.length > 0;
  const grantsSystemAccess = offersSystemAccess && systemAccess;

  // Nothing is loaded from the server, so "dirty" is simply "anything entered".
  const isDirty =
    Boolean(email.trim() || firstName.trim() || lastName.trim()) || selectedRoleIds.size > 0 || systemAccess;
  const guard = useDiscardGuard({
    isDirty,
    isBusy: isSaving,
    onClose,
    recordNoun: 'this invitation',
  });

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
    setRoleSelectionError(null);
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    let valid = true;

    // Required by the API, so the dialog asks for them. They are discarded for
    // somebody who already has an account, whose own name wins — the dialog
    // cannot tell which case it is in without asking the server first.
    const names = {
      first: trimmedFirst ? null : 'First name is required.',
      last: trimmedLast ? null : 'Last name is required.',
    };
    setNameError(names);
    if (names.first || names.last) valid = false;
    if (!trimmedEmail) {
      setEmailError('Email is required.');
      valid = false;
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      valid = false;
    } else {
      setEmailError(null);
    }
    if (selectedRoleIds.size === 0 && !grantsSystemAccess) {
      setRoleSelectionError('Select at least one role.');
      valid = false;
    } else {
      setRoleSelectionError(null);
    }
    if (!valid) return;

    setIsSaving(true);
    setSubmitError(null);
    try {
      const roleIds = [
        ...Array.from(selectedRoleIds),
        ...(grantsSystemAccess ? systemRoles.map((role) => role.id) : []),
      ];
      const result = await apiService.inviteOrganizationMember(
        orgId,
        { email: trimmedEmail, firstName: trimmedFirst, lastName: trimmedLast, roleIds },
        accessToken,
      );
      toast.success(
        result.status === 'invited'
          ? `Invitation sent to ${result.email}`
          : `${result.email} has been added to the organization`,
      );
      onSuccess(result, { firstName: trimmedFirst, lastName: trimmedLast });
      onClose();
    } catch (err: unknown) {
      console.error('Failed to add member:', err);
      setSubmitError(humanizeError(err, "Couldn't add the member"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} pending={isSaving} onOpenChange={guard.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite member</DialogTitle>
          <DialogDescription>
            Add someone to this organization. If they don't have a Dory account yet, they will be
            emailed a link to set their own password.
          </DialogDescription>
        </DialogHeader>

        <DialogForm
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
        <DialogBody className="space-y-4">
          <Field label="Email address" error={emailError} required>
            {(control) => (
              <Input
                {...control}
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                disabled={isSaving}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name" error={nameError.first} required>
              {(control) => (
                <Input
                  {...control}
                  value={firstName}
                  placeholder="Ada"
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (nameError.first) setNameError((prev) => ({ ...prev, first: null }));
                  }}
                  disabled={isSaving}
                />
              )}
            </Field>
            <Field label="Last name" error={nameError.last} required>
              {(control) => (
                <Input
                  {...control}
                  value={lastName}
                  placeholder="Lovelace"
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (nameError.last) setNameError((prev) => ({ ...prev, last: null }));
                  }}
                  disabled={isSaving}
                />
              )}
            </Field>
          </div>

          <RoleChecklist
            idPrefix="invite-role"
            roles={standardRoles}
            selectedIds={selectedRoleIds}
            onToggle={toggleRole}
            disabled={isSaving}
            isLoading={isLoadingRoles}
            loadError={rolesError}
            onRetry={loadRoles}
            required
            error={roleSelectionError}
          />

          {offersSystemAccess && !isLoadingRoles && rolesError == null && (
            <>
              <SystemAccessSwitch
                checked={systemAccess}
                disabled={isSaving}
                onCheckedChange={(checked) => {
                  setSystemAccess(checked);
                  if (checked) setRoleSelectionError(null);
                }}
              />
              {/* The invite's own submit is the confirmation, so the warning
                  sits here rather than in a second dialog. */}
              {grantsSystemAccess ? (
                <ConsequenceAlert>{systemAccessConsequence('this person')}</ConsequenceAlert>
              ) : null}
            </>
          )}
        </DialogBody>

        <FormError message={submitError} />
        <DialogActions>
          <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSaving}>Cancel</Button>
          <Button type="submit" loading={isSaving} disabled={isLoadingRoles}>
            Invite member
          </Button>
        </DialogActions>
        </DialogForm>
      </DialogContent>
    </Dialog>
    {guard.guardDialog}
    </>
  );
};

// ---------------------------------------------------------------------------
// Role assignment dialog
// ---------------------------------------------------------------------------

interface RoleAssignDialogProps {
  open: boolean;
  targetUser: OrganizationUser | null;
  orgId: string;
  /** Whether `orgId` is the system organization — the only place the switch exists. */
  isSystemOrg: boolean;
  accessToken: string;
  onClose: () => void;
  /**
   * Called after a save (and after a partial one) with the member's roles as
   * the server now holds them, so the page patches that row's `roles` in place.
   */
  onSuccess: (roles: MemberRole[]) => void;
  /** org_roles:read — whether "Roles page" in the help line is a link (page-level boolean). */
  canOpenRoles: boolean;
}

/** Which one confirmation Save opens, if any (DESIGN_SYSTEM §5; rules of record 11). */
type SaveConfirmKind = 'remove' | 'grant' | 'both';

/**
 * Manage roles is a STAGED surface. Ticking a role, unticking one and flipping
 * the system administrator access switch all change a draft and send nothing;
 * "Save changes" commits the difference. Save asks once, and only when the
 * difference removes something or grants system administrator access — the
 * grant is confirmed exactly as its removal is. Additions of ordinary roles
 * alone need no dialog.
 *
 * Grants run before revokes, one request at a time, and stop at the first
 * refusal: swapping the last members-admin role for another only succeeds in
 * that order. After a refusal the confirmed state is reloaded from the server,
 * the draft is kept, and the error shows inline, so what is still unsaved stays
 * visible and Save retries exactly that.
 */
const RoleAssignDialog: React.FC<RoleAssignDialogProps> = ({
  open, targetUser, orgId, isSystemOrg, accessToken, onClose, onSuccess, canOpenRoles,
}) => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  /** Every role id the member holds on the server, system roles included. */
  const [heldIds, setHeldIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [systemAccess, setSystemAccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmKind, setConfirmKind] = useState<SaveConfirmKind | null>(null);

  /**
   * `resetDraft` on open only. A reload after a failed save refreshes what the
   * server holds but keeps the draft, so the remaining difference stays shown.
   */
  /** Resolves to the member's roles as the server holds them, or null when the read failed. */
  const load = useCallback(async (
    { resetDraft, silent }: { resetDraft: boolean; silent?: boolean },
  ): Promise<MemberRole[] | null> => {
    if (!targetUser) return null;
    if (!silent) setIsLoading(true);
    setLoadError(null);
    try {
      const [roleList, assignmentList] = await Promise.all([
        apiService.listOrgRoles(orgId, accessToken),
        apiService.listUserRoles(orgId, targetUser.userId, accessToken),
      ]);
      const held = new Set<string>(assignmentList.map((a: UserRoleAssignment) => a.roleId));
      setRoles(roleList);
      setHeldIds(held);
      if (resetDraft) {
        setSelectedIds(new Set(roleList.filter((r) => !r.isSystemOnly && held.has(r.id)).map((r) => r.id)));
        setSystemAccess(roleList.some((r) => r.isSystemOnly && held.has(r.id)));
      }
      return memberRolesFrom(assignmentList, roleList, isSystemOrg);
    } catch (err: unknown) {
      console.error('Failed to load member roles:', err);
      setLoadError(err);
      return null;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [orgId, accessToken, targetUser, isSystemOrg]);

  useEffect(() => {
    if (!open || !targetUser) return;
    setSaveError(null);
    setConfirmKind(null);
    load({ resetDraft: true });
  }, [open, targetUser, load]);

  const { standardRoles, systemRoles } = useMemo(() => partitionRoles(roles), [roles]);
  const offersSystemAccess = isSystemOrg && systemRoles.length > 0;
  // The dialog opens for a member who has not accepted their invitation — the
  // invite assigned their roles before any sign-in, so there is something real
  // to show and change. What it must not do is read as though those roles are
  // in force: nobody is using them yet.
  const isPending = targetUser?.status === 'invited';

  const displayName =
    [targetUser?.firstName, targetUser?.lastName].filter(Boolean).join(' ') || targetUser?.email || 'this member';

  // The staged difference between the draft and the server.
  const confirmedSystem = systemRoles.some((r) => heldIds.has(r.id));
  const addedRoles = standardRoles.filter((r) => selectedIds.has(r.id) && !heldIds.has(r.id));
  const removedRoles = standardRoles.filter((r) => !selectedIds.has(r.id) && heldIds.has(r.id));
  const grantsSystem = offersSystemAccess && systemAccess && !confirmedSystem;
  const removesSystem = offersSystemAccess && !systemAccess && confirmedSystem;
  const changeCount = addedRoles.length + removedRoles.length + (grantsSystem || removesSystem ? 1 : 0);
  const isDirty = changeCount > 0;
  // The staged draft is never dropped silently: Escape, outside click, X and
  // Cancel route through the portal-wide discard guard (ruling 11).
  const guard = useDiscardGuard({
    isDirty,
    isBusy: isSaving,
    onClose,
    recordNoun: `${displayName}'s roles`,
  });
  const holdsEveryRole = standardRoles.length > 0 && standardRoles.every((r) => selectedIds.has(r.id));

  const toggleRole = (roleId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  /** Throws on the first refusal, so an open ConfirmDialog stays open with it. */
  const runSave = async () => {
    if (!targetUser) return;
    const toAdd = [
      ...addedRoles.map((r) => r.id),
      ...(grantsSystem ? systemRoles.filter((r) => !heldIds.has(r.id)).map((r) => r.id) : []),
    ];
    const toRemove = [
      ...removedRoles.map((r) => r.id),
      ...(removesSystem ? systemRoles.filter((r) => heldIds.has(r.id)).map((r) => r.id) : []),
    ];
    setIsSaving(true);
    setSaveError(null);
    try {
      for (const roleId of toAdd) {
        await apiService.assignUserRole(orgId, targetUser.userId, roleId, accessToken);
      }
      for (const roleId of toRemove) {
        await apiService.revokeUserRole(orgId, targetUser.userId, roleId, accessToken);
      }
    } catch (err: unknown) {
      console.error('Failed to save role changes:', err);
      setSaveError(humanizeError(err, "Couldn't save all role changes"));
      // Some requests may have landed before the refusal: re-read the truth,
      // and patch the row only from that read (the draft is not the truth).
      const reloaded = await load({ resetDraft: false, silent: true });
      if (reloaded) onSuccess(reloaded);
      setIsSaving(false);
      throw err;
    }
    // Every write landed. Patch the row from the server's roles; if that one
    // read fails, the saved draft IS what the server now holds.
    const saved = (await load({ resetDraft: false, silent: true })) ?? stagedMemberRoles();
    setIsSaving(false);
    toast.success('Roles updated');
    onSuccess(saved);
    onClose();
  };

  /** The draft as roles: ordinary roles ticked, plus system access when switched on. */
  const stagedMemberRoles = (): MemberRole[] => [
    ...(offersSystemAccess && systemAccess ? systemRoles : []),
    ...standardRoles.filter((r) => selectedIds.has(r.id)),
  ].map((r) => ({ id: r.id, name: r.name, isSystemOnly: r.isSystemOnly }));

  const handleSave = () => {
    const removes = removedRoles.length > 0 || removesSystem;
    if (removes && grantsSystem) setConfirmKind('both');
    else if (removes) setConfirmKind('remove');
    else if (grantsSystem) setConfirmKind('grant');
    else runSave().catch(() => undefined); // the refusal is already shown inline
  };

  const removedNames = [
    ...removedRoles.map((r) => r.name),
    ...(removesSystem ? [SYSTEM_ACCESS_LABEL] : []),
  ];
  const removalSentence = removedNames.length > 0 ? (
    <>
      Removes {removedNames.map((name, index) => (
        <React.Fragment key={name}>
          {index > 0 ? ', ' : null}
          <strong>{name}</strong>
        </React.Fragment>
      ))} from {displayName}.{' '}
      {isPending
        ? 'They will not have the permissions these grant when they sign in.'
        : 'They will immediately lose the permissions these grant.'}
    </>
  ) : null;
  const grantSentence = isPending
    ? `${displayName} gains System Admin and everything it reaches across organizations when they sign in.`
    : `${displayName} gains System Admin and everything it reaches across organizations.`;

  const confirmCopy: Record<SaveConfirmKind, { title: string; label: string; variant: 'destructive' | 'default' }> = {
    remove: { title: 'Remove roles', label: 'Remove and save', variant: 'destructive' },
    grant: { title: 'Grant system administrator access', label: 'Grant access', variant: 'default' },
    both: { title: 'Confirm role changes', label: 'Save changes', variant: 'destructive' },
  };

  return (
    <>
      <Dialog
        open={open}
        pending={isSaving}
        onOpenChange={guard.onOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage roles</DialogTitle>
            <DialogDescription>{displayName}</DialogDescription>
          </DialogHeader>

          <DialogBody>
          {isLoading ? (
            <div className="space-y-3 rounded-md border p-3" aria-busy="true">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          ) : loadError != null ? (
            <ErrorState
              compact
              title="Couldn't load this member's roles"
              error={loadError}
              onRetry={() => load({ resetDraft: true })}
            />
          ) : (
            <div className="space-y-4">
              {isPending && (
                <Alert variant="info">
                  <Info aria-hidden="true" />
                  <AlertTitle>This invitation is still open</AlertTitle>
                  <AlertDescription>
                    {displayName} has not signed in yet. These are the roles they will hold when
                    they do — changing them now is the same as changing them later.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <RoleChecklist
                  idPrefix="manage-role"
                  roles={standardRoles}
                  selectedIds={selectedIds}
                  onToggle={toggleRole}
                  disabled={isSaving}
                  help={holdsEveryRole ? (
                  <>
                    {displayName} holds every role in this organization. Create a custom role on the{' '}
                    {canOpenRoles ? (
                      // A real link (middle-click and new tab work), but a plain
                      // click goes through the discard guard: leaving the page
                      // would drop a staged draft silently.
                      <Link
                        to="/my-org/roles"
                        className="rounded-sm font-medium text-foreground underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={(event) => {
                          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                          event.preventDefault();
                          guard.confirmDiscard(() => navigate('/my-org/roles'));
                        }}
                      >
                        Roles page
                      </Link>
                    ) : (
                      'Roles page'
                    )}{' '}
                    to grant narrower access.
                  </>
                ) : undefined}
                />
                {/* Under the checklist, where the refused change is (rules of record 11). */}
                <FormError message={saveError} />
              </div>

              {offersSystemAccess && (
                <SystemAccessSwitch
                  checked={systemAccess}
                  disabled={isSaving}
                  onCheckedChange={setSystemAccess}
                />
              )}
            </div>
          )}
          </DialogBody>

          <DialogActions
            status={isDirty ? `${changeCount} ${changeCount === 1 ? 'role changes' : 'roles change'}` : null}
          >
            <Button type="button" variant="outline" onClick={guard.requestClose} disabled={isSaving}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isDirty || isLoading || loadError != null}
            >
              Save changes
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmKind !== null}
        title={confirmKind ? confirmCopy[confirmKind].title : ''}
        description={
          confirmKind === 'grant' ? (
            grantSentence
          ) : confirmKind === 'both' ? (
            <>
              {removalSentence} {grantSentence}
            </>
          ) : (
            removalSentence ?? ''
          )
        }
        consequence={
          confirmKind === 'grant' || confirmKind === 'both'
            ? systemAccessConsequence(displayName)
            : undefined
        }
        confirmLabel={confirmKind ? confirmCopy[confirmKind].label : 'Save changes'}
        variant={confirmKind ? confirmCopy[confirmKind].variant : 'destructive'}
        errorContext="Couldn't save all role changes"
        onConfirm={runSave}
        onClose={() => setConfirmKind(null)}
      />

      {guard.guardDialog}
    </>
  );
};

// ---------------------------------------------------------------------------

/**
 * The Members table's sorts: Name (last name, then email — the API's `name`),
 * Status, Joined and Invited. Roles is not sortable. A bookmarked ?sort=email,
 * role, firstName or lastName falls back to the default.
 */
const SORT_KEYS = ['name', 'status', 'joinedAt', 'createdAt'] as const;
type SortBy = (typeof SORT_KEYS)[number];

const INITIAL_SORT = { column: 'createdAt', direction: 'desc' } as const;

/**
 * A member's assignments as the Roles column shows them: names and the system
 * flag from the organization's role list. Outside the system organization a
 * system-only role is left out, as the API's people reads leave it out.
 */
function memberRolesFrom(
  assignments: UserRoleAssignment[],
  roleList: Role[],
  isSystemOrg: boolean,
): MemberRole[] {
  const byId = new Map(roleList.map((r) => [r.id, r]));
  return assignments
    .map((a) => {
      const role = byId.get(a.roleId);
      return { id: a.roleId, name: role?.name ?? a.roleName, isSystemOnly: role?.isSystemOnly ?? false };
    })
    .filter((r) => isSystemOrg || !r.isSystemOnly);
}

const MyOrg: React.FC = () => {
  const { selectedOrganization, isLoading: orgLoading, refreshOrganizations } = useOrganization();
  const { accessToken, user: currentUser } = useUser();
  const { hasPermission } = usePermissions();

  const canManageRoles = hasPermission('org_members:write');

  const orgId = selectedOrganization?.id ?? null;

  /*
   * The final Members column set (rules of record 7): Name, Roles, Status,
   * Joined (membership accepted), Invited (membership created, hideable), then
   * the actions. The legacy organization_users.role label is never shown.
   * Declared before the list: the column picker decides the initial sort.
   */
  const columns = useMemo<Array<DataTableColumn<OrganizationUser>>>(() => [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (u) => <PersonCell firstName={u.firstName} lastName={u.lastName} email={u.email} />,
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (u) => <RoleChips roles={u.roles} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      // A plain badge: Resend invitation is a visible row action, never a
      // control that appears only on hover (DESIGN_SYSTEM.md §6.1).
      render: (u) => <StatusBadge status={u.status} />,
    },
    {
      key: 'joinedAt',
      header: 'Joined',
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (u) => <DateCell value={u.joinedAt} />,
    },
    {
      key: 'createdAt',
      header: 'Invited',
      label: 'Invited',
      hideable: true,
      sortable: true,
      className: 'whitespace-nowrap text-muted-foreground',
      render: (u) => <DateCell value={u.createdAt} />,
    },
  ], []);

  const columnVisibility = useColumnVisibility<OrganizationUser>({
    tableId: 'my-org-members',
    columns,
    defaultSort: INITIAL_SORT,
  });

  const membersQuery = useInfiniteList<OrganizationUser, SortBy>({
    ...batchFetcher<OrganizationUser, SortBy>(async ({ limit, offset, cursor, search, sort, signal }) => {
      const response = await apiService.getOrganizationUsers(orgId as string, accessToken as string, {
        limit,
        offset,
        cursor,
        search,
        sortBy: sort?.column ?? INITIAL_SORT.column,
        sortDirection: sort?.direction ?? INITIAL_SORT.direction,
        signal,
      });
      return { items: response.users, pagination: response.pagination };
    }),
    rowKey: (u) => u.userId,
    enabled: Boolean(orgId && accessToken),
    resetKey: orgId,
    initialSort: columnVisibility.initialSort as { column: SortBy; direction: 'asc' | 'desc' },
    sortColumns: SORT_KEYS,
    errorContext: "Couldn't load organization members",
    urlState: true,
  });
  const { search, searchInput, setSearchInput, clearSearch, removeItem, prependItem, patchItem } = membersQuery;

  // Invite member dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  /*
   * Manage roles opens from component state, like Invite and Remove beside it.
   * It used to be driven by ?manageRoles=<userId>, which could only find a
   * member among the rows already loaded — a reload or a pasted link to anyone
   * past them opened nothing and left the param behind. The row button always
   * holds the full row, so there is nothing to look up.
   */
  const [roleDialogUser, setRoleDialogUser] = useState<OrganizationUser | null>(null);

  // Remove member dialog state
  const [removeDialogUser, setRemoveDialogUser] = useState<OrganizationUser | null>(null);

  const handleRemoveMember = async () => {
    if (!removeDialogUser || !selectedOrganization?.id || !accessToken) return;
    await apiService.removeOrganizationMember(selectedOrganization.id, removeDialogUser.userId, accessToken);
    toast.success('Member removed');
    removeItem(removeDialogUser.userId);
  };

  /**
   * An invite answers with ids and status only, so the member's real row is
   * read by its address (a one-row search, not a reload of the list) and pinned
   * first marked New. If that read fails the row is built from what the dialog
   * sent — nothing is invented: unknown dates stay empty.
   */
  const handleInvited = useCallback(
    async (result: InviteMemberResponse, names: { firstName: string; lastName: string }) => {
      if (!orgId || !accessToken) return;
      try {
        const response = await apiService.getOrganizationUsers(orgId, accessToken, {
          limit: 5,
          offset: 0,
          search: result.email,
          sortBy: 'createdAt',
          sortDirection: 'desc',
        });
        const row = response.users.find((u) => u.userId === result.userId);
        if (row) {
          prependItem(row, { mark: 'new' });
          return;
        }
      } catch (err: unknown) {
        console.error('Failed to read the invited member:', err);
      }
      prependItem(
        {
          userId: result.userId,
          email: result.email,
          firstName: names.firstName || null,
          lastName: names.lastName || null,
          // Roles as the invite granted them are not known here (only ids came
          // back); the next reset shows them. Never the legacy label.
          roles: [],
          status: result.status,
          invitedByUserId: currentUser?.id ?? null,
          joinedAt: null,
          createdAt: '',
        },
        { mark: 'new' },
      );
    },
    [orgId, accessToken, prependItem, currentUser?.id],
  );

  // Re-sending an invitation: one row at a time, tracked by user id so the
  // cell can hold the spinner in place of the chip.
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const resendInvitation = useCallback(async (member: OrganizationUser) => {
    if (!selectedOrganization?.id || !accessToken) return;
    setResendingUserId(member.userId);
    try {
      await apiService.resendOrganizationInvitation(selectedOrganization.id, member.userId, accessToken);
      toast.success(`Invitation resent to ${member.email}`);
    } catch (err: unknown) {
      console.error('Failed to resend invitation:', err);
      // A 409 means the invitation is no longer open — accepted, or a password
      // set — since the list was loaded. The chip is stale, not the request:
      // say so, rather than the generic 409 wording. Nothing reloads (ruling
      // 20); the row shows its current status after the next reset.
      if (classifyError(err).kind === 'conflict') {
        toast.error('This invitation has already been accepted, so there is nothing to resend.');
        return;
      }
      toast.error(humanizeError(err, "Couldn't resend the invitation"));
    } finally {
      setResendingUserId(null);
    }
  }, [selectedOrganization?.id, accessToken]);

  /*
   * Members have no record dialog of their own, so rows are inert (no onOpenRow,
   * no hover) and carry only task actions. Readers get no Actions column at all.
   */
  const memberRowActions = (u: OrganizationUser) => (
    <>
      {u.status === 'invited' ? (
        <RowAction
          icon={MailPlus}
          label="Resend invitation"
          aria-label={`Resend invitation to ${u.email}`}
          loading={resendingUserId === u.userId}
          onClick={() => void resendInvitation(u)}
        />
      ) : null}
      <RowAction
        icon={ShieldCheck}
        label="Manage roles"
        aria-label={`Manage roles for ${u.email}`}
        onClick={() => setRoleDialogUser(u)}
      />
      {u.userId !== currentUser?.id ? (
        <RowAction
          icon={UserMinus}
          label="Remove member"
          aria-label={`Remove ${u.email} from organization`}
          tone="destructive"
          onClick={() => setRemoveDialogUser(u)}
        />
      ) : null}
    </>
  );


  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="shrink-0">
      <PageHeader
        title={PAGE.title}
        description={PAGE.description}
        actions={
          canManageRoles ? (
            <Button onClick={() => setInviteDialogOpen(true)}>
              <UserPlus aria-hidden="true" />
              Invite member
            </Button>
          ) : undefined
        }
      />
      </div>

      {!selectedOrganization && !orgLoading ? (
        <EmptyState
          icon={Building2}
          title="No organization selected"
          description="Select an organization from the switcher to see its members."
        />
      ) : (
        // The error renders inside the card, below the toolbar, so search and
        // the count strip stay put (ruling 19).
        <DataTable<OrganizationUser>
          {...listTableProps(membersQuery)}
          listState={orgLoading ? 'initial' : membersQuery.status}
          columns={columnVisibility.columns}
          columnPicker={
            <ColumnPicker
              columns={columns}
              state={columnVisibility}
              sortGuard={{ activeSort: membersQuery.sort, onSortChange: membersQuery.onSortChange }}
            />
          }
          rowKey={(u) => u.userId}
          listLabel="Members"
          itemNoun="members"
          rowActions={canManageRoles ? memberRowActions : undefined}
          searchable
          search={
            <SearchInput
              value={searchInput}
              onChange={setSearchInput}
              noun="members"
              placeholder="Search by name or email…"
              shortcut
            />
          }
          errorState={
            hasListError(membersQuery) ? (
              <ErrorState
                title="Couldn't load organization members"
                error={membersQuery.error}
                onRetry={membersQuery.reset}
                isRetrying={isListRetrying(membersQuery)}
                action={search ? <Button variant="outline" size="sm" onClick={clearSearch}>Clear search</Button> : undefined}
              />
            ) : undefined
          }
          emptyState={
            <ListEmptyState
              icon={Users}
              noun="members"
              search={search}
              onClearSearch={clearSearch}
              firstRun={{
                title: 'No members yet',
                description: 'Invite teammates to collaborate in this organization.',
                action: canManageRoles ? (
                  <Button onClick={() => setInviteDialogOpen(true)}>
                    <UserPlus aria-hidden="true" />
                    Invite member
                  </Button>
                ) : undefined,
              }}
            />
          }
        />
      )}

      {canManageRoles && selectedOrganization && accessToken && (
        <InviteMemberDialog
          open={inviteDialogOpen}
          orgId={selectedOrganization.id}
          isSystemOrg={selectedOrganization.isSystemOrg === true}
          accessToken={accessToken}
          onClose={() => setInviteDialogOpen(false)}
          onSuccess={(result, names) => void handleInvited(result, names)}
        />
      )}

      {canManageRoles && selectedOrganization && accessToken && (
        <RoleAssignDialog
          open={roleDialogUser !== null}
          targetUser={roleDialogUser}
          orgId={selectedOrganization.id}
          isSystemOrg={selectedOrganization.isSystemOrg === true}
          accessToken={accessToken}
          canOpenRoles={hasPermission('org_roles:read')}
          onClose={() => setRoleDialogUser(null)}
          // The member's row gets its `roles` from the server, marked Updated;
          // the loaded rows, search, sort and scroll position all stay (ruling 20).
          // Nothing reloads. Only a change to the CALLER's own roles can change
          // what this session may do, so only then are permissions re-read — in
          // the background, which never swaps the page for the route skeleton
          // (a foreground refresh remounted the page and threw the patch away).
          onSuccess={(roles) => {
            if (!roleDialogUser) return;
            patchItem(roleDialogUser.userId, (row) => ({ ...row, roles }), { mark: 'updated' });
            if (roleDialogUser.userId === currentUser?.id) void refreshOrganizations({ background: true });
          }}
        />
      )}

      {canManageRoles && selectedOrganization && accessToken && (
        <ConfirmDialog
          open={removeDialogUser !== null}
          title={`Remove "${
            (removeDialogUser && personDisplayName(removeDialogUser)) ||
            removeDialogUser?.email ||
            'this member'
          }"?`}
          description="They lose access to this organization and every role they hold in it. Their Dory account is not deleted."
          confirmLabel="Remove member"
          errorContext="Couldn't remove the member"
          onConfirm={handleRemoveMember}
          onClose={() => setRemoveDialogUser(null)}
        />
      )}
    </div>
  );
};

export default MyOrg;
